'use client';

import { useTranslations } from 'next-intl';
import React, { useMemo } from 'react';

import PreferenceToggle from './PreferenceToggle';
import { NewsletterTopic, NewsletterTopicGroup, useNewsletterTopics } from './useNewsletterTopics';

export function topicKey(type: string, referenceId?: string | null): string {
    return `${type}:${referenceId ?? ''}`;
}

const GROUP_ORDER: NewsletterTopicGroup[] = ['program', 'highlights_specials'];

const GROUP_LABEL_KEY: Record<NewsletterTopicGroup, string> = {
    program: 'newsletter-preferences.group.program',
    highlights_specials: 'newsletter-preferences.group.highlights-specials'
};

type Props = {
    desiredKeys: Set<string>;
    onToggleTopic: (topic: NewsletterTopic) => void;
    /** Replaces the desired set entirely (used by the per-group "All" toggle). */
    onSetGroup: (group: NewsletterTopicGroup, allOn: boolean) => void;
    disabled?: boolean;
};

/**
 * Renders newsletter topic toggles grouped by section, with an "All" toggle
 * in each group header that batch-flips every topic in that group.
 * Used by both the registration view and the preferences view.
 */
export default function GroupedTopicToggles({ desiredKeys, onToggleTopic, onSetGroup, disabled = false }: Props) {
    const t = useTranslations();
    const { allTopics } = useNewsletterTopics();

    const grouped = useMemo(() => {
        const out: Record<NewsletterTopicGroup, NewsletterTopic[]> = {
            program: [],
            highlights_specials: []
        };
        allTopics.forEach((topic) => out[topic.group].push(topic));
        return out;
    }, [allTopics]);

    return (
        <div className="flex flex-col gap-32">
            {GROUP_ORDER.map((group) => {
                const topics = grouped[group];
                if (topics.length === 0) return null;
                const allOn = topics.every((tp) => desiredKeys.has(topicKey(tp.type, tp.referenceId)));
                return (
                    <section
                        key={group}
                        className="flex flex-col gap-16">
                        <div className="flex items-center justify-between">
                            <h2 className="text-body-bold-14 text-[var(--on-background-color)]">
                                {t(GROUP_LABEL_KEY[group])}
                            </h2>
                            <div className="flex items-center gap-8">
                                <span className="text-body-14 text-[var(--on-background-color)]">
                                    {t('newsletter-preferences.all')}
                                </span>
                                <PreferenceToggle
                                    label=""
                                    checked={allOn}
                                    disabled={disabled}
                                    onChange={(next) => onSetGroup(group, next)}
                                    hideLabel
                                />
                            </div>
                        </div>
                        <div className="flex flex-col gap-16">
                            {topics.map((topic) => {
                                const k = topicKey(topic.type, topic.referenceId);
                                return (
                                    <PreferenceToggle
                                        key={k}
                                        label={topic.label}
                                        description={topic.description}
                                        checked={desiredKeys.has(k)}
                                        disabled={disabled}
                                        onChange={() => onToggleTopic(topic)}
                                    />
                                );
                            })}
                        </div>
                    </section>
                );
            })}
        </div>
    );
}
